import { useDragging, useSelectionReference, useMedia } from "@/hooks/contextHooks"
import { subscribeToContentStore, getContentStoreSnapshot } from "@/contexts/contexts"
import { Badge, Box, Button, Center, Flex, IconButton, Input, Menu, MenuButton, MenuItem, MenuList, MenuDivider, Portal, Text, Tooltip, useColorModeValue } from "@chakra-ui/react"
import { faCancel, faCheck, faCrown, faEllipsis, faFilePdf, faQuestion, faVideo, faMusic, faListCheck, faImage, faPen, faTrash, faTimes, faTags } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { ContentMeta, NewContentItem, NewMediaReference, NewSection } from "@xnav/types/src/common"
import React, { memo, MouseEventHandler, useEffect, useMemo, useState, useSyncExternalStore } from "react"
import { ReactStateType } from "@xnav/types/src/frontend"
import { EditContent } from "@/pages/editor"

const ContentCardComponent: React.FC<{
	index: number,
	contentMeta: NewMediaReference,
	validateName: (name: string) => boolean,
	onChange: (newContent: NewMediaReference) => void,
	onDelete: (index: number) => void,
	onMoveItem: (draggedId: string, targetId: string, position: 'inside' | 'before' | 'after') => void
}> = ({ index, contentMeta, validateName, onChange, onDelete, onMoveItem }) => {

	const [dragging, setDragging] = useDragging()
	const [selectionReference, setSelectionReference] = useSelectionReference() as ReactStateType<NewContentItem>
	const [media] = useMedia() as [any[] | undefined, any]

	const [newName, setNewName] = useState<string>()
	const [confirmDeletion, setConfirmDeletion] = useState<boolean>(false)
	const [isValid, setIsValid] = useState(false)
	const [isHovering, setIsHovering] = useState(false)
	const [isDraggingSelf, setIsDraggingSelf] = useState(false)
	const [dropPosition, setDropPosition] = useState<'before' | 'after' | null>(null)

	// Subscribe only to relevant metadata for this specific content item (video, pdf, audio).
	// When quiz question text, options, or explanations change, non-quiz items return identical snapshots
	// and quiz items return empty strings, preventing re-renders!
	const itemSummary = useSyncExternalStore(
		subscribeToContentStore,
		() => {
			if (contentMeta.type !== "video" && contentMeta.type !== "pdf" && contentMeta.type !== "audio") {
				return "";
			}
			const item = getContentStoreSnapshot()[contentMeta.id];
			if (!item) return "";
			const vId = (contentMeta as any).videoId || item.videoId || "";
			const vTitle = (contentMeta as any).videoTitle || item.videoTitle || "";
			const polyLen = item.polygons?.length || 0;
			const spotLen = item.hotSpots?.length || 0;
			const docTitle = (item as any).docTitle || "";
			const audioTitle = (item as any).audioTitle || "";
			return `${vId}:${vTitle}:${polyLen}:${spotLen}:${docTitle}:${audioTitle}`;
		}
	);

	const loadedContent = useMemo(() => {
		if (contentMeta.type !== "video" && contentMeta.type !== "pdf" && contentMeta.type !== "audio") {
			return undefined;
		}
		return getContentStoreSnapshot()[contentMeta.id];
	}, [contentMeta.id, contentMeta.type, itemSummary]);

	const isMediaReferenced = useMemo(() => {
		if (!media || !Array.isArray(media)) return false;
		return media.some(m => m.references?.includes(contentMeta.id) || m.name === contentMeta.title || m.id === contentMeta.id);
	}, [media, contentMeta.id, contentMeta.title]);

	const hasVideo = useMemo(() => {
		if (contentMeta.type !== "video") return true;
		const vId = (contentMeta as any).videoId || loadedContent?.videoId;
		const vTitle = (contentMeta as any).videoTitle || loadedContent?.videoTitle;
		return Boolean(vId || vTitle || isMediaReferenced);
	}, [contentMeta, loadedContent, isMediaReferenced]);

	const tagCount = useMemo(() => {
		const polys = loadedContent?.polygons || (contentMeta as any).polygons || [];
		const spots = loadedContent?.hotSpots || (contentMeta as any).hotSpots || [];
		return polys.length + spots.length;
	}, [contentMeta, loadedContent]);

	const icon = useMemo(() => {
		switch (contentMeta.type) {
			case "video":
				return faVideo
			case "pdf":
				return faFilePdf
			case "audio":
				return faMusic
			case "quiz":
				return faListCheck
			case "image":
				return faImage
			default:
				return faQuestion
		}
	}, [contentMeta])

	const iconColor = useMemo(() => {
		if (contentMeta.type === "video") {
			return hasVideo ? "green.500" : "red.500";
		}
		if (contentMeta.type === "pdf") {
			const hasPdf = Boolean((contentMeta as any).fileId || (contentMeta as any).docId || (contentMeta as any).docTitle || (loadedContent as any)?.docTitle || isMediaReferenced);
			return hasPdf ? "teal.500" : "red.500";
		}
		if (contentMeta.type === "audio") {
			const hasAudio = Boolean((contentMeta as any).fileId || (contentMeta as any).audioId || (contentMeta as any).audioTitle || (loadedContent as any)?.audioTitle || isMediaReferenced);
			return hasAudio ? "teal.500" : "red.500";
		}
		switch (contentMeta.type) {
			case "quiz": return "purple.500";
			case "flashcard": return "orange.500";
			case "image": return "blue.500";
			default: return "gray.500";
		}
	}, [contentMeta, hasVideo, loadedContent, isMediaReferenced])

	const premiumColor = useMemo(() => {
		switch (contentMeta.premium) {
			case 1: return "#48BB78" // green.400
			case 2: return "#4299E1" // blue.400
			case 3: return "#ECC94B" // yellow.400
			default: return "gray"
		}
	}, [contentMeta.premium])

	const isActive = selectionReference && selectionReference.id === contentMeta.id;

	const handleDragStart = (e: React.DragEvent) => {
		e.stopPropagation()
		e.dataTransfer.setData("text/plain", contentMeta.id)
		e.dataTransfer.effectAllowed = "move"
		setDragging({
			id: contentMeta.id,
			data: contentMeta,
			onDragEnd: () => {}
		})
		setTimeout(() => {
			setIsDraggingSelf(true)
		}, 0)
	}

	const handleDragEnd = (e: React.DragEvent) => {
		setDragging(undefined)
		setIsDraggingSelf(false)
	}

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault()
		e.stopPropagation()

		if (dragging && dragging.id === contentMeta.id) {
			return
		}

		const rect = e.currentTarget.getBoundingClientRect()
		const relativeY = e.clientY - rect.top
		const height = rect.height

		let position: 'before' | 'after' = 'after'
		if (relativeY < height * 0.5) {
			position = 'before'
		}

		setDropPosition(position)
	}

	const handleDragLeave = (e: React.DragEvent) => {
		setDropPosition(null)
	}

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault()
		e.stopPropagation()
		setDropPosition(null)

		const draggedId = e.dataTransfer.getData("text/plain") || (dragging as any)?.id
		if (draggedId && draggedId !== contentMeta.id) {
			const rect = e.currentTarget.getBoundingClientRect()
			const relativeY = e.clientY - rect.top
			const height = rect.height

			let position: 'before' | 'after' = 'after'
			if (relativeY < height * 0.5) {
				position = 'before'
			}

			onMoveItem(draggedId, contentMeta.id, position)
		}
	}

	useEffect(() => {
		if (newName !== undefined) setIsValid(validateName(newName))
	}, [newName])



	return (
		<Button
			as="div"
			onMouseEnter={() => setIsHovering(true)}
			onMouseLeave={() => setIsHovering(false)}
			draggable={true}
			onDragStart={handleDragStart}
			onDragEnd={handleDragEnd}
			onDragOver={handleDragOver}
			onDragLeave={handleDragLeave}
			onDrop={handleDrop}
			isActive={isActive}
			w='full'
			variant='ghost'
			bg={isActive ? useColorModeValue("teal.50", "rgba(16, 185, 129, 0.15)") : "transparent"}
			_hover={{ bg: isActive ? useColorModeValue("teal.50", "rgba(16, 185, 129, 0.15)") : useColorModeValue("gray.100", "gray.800") }}
			borderRadius="lg"
			flexDir='row'
			gap={3}
			px={3}
			py={2.5}
			size='sm'
			cursor="pointer"
			onClick={() => {
				setSelectionReference(contentMeta)
			}}
			position="relative"
			style={{
				opacity: isDraggingSelf ? 0.4 : 1
			}}
		>
			{isActive && (
				<Box position="absolute" left={0} top={0} bottom={0} w="4px" bg="teal.500" borderTopLeftRadius="md" borderBottomLeftRadius="md" />
			)}
			{dropPosition === 'before' && (
				<Box position="absolute" top="-2px" left="0" right="0" height="4px" bg="purple.500" zIndex="10" borderRadius="full" pointerEvents="none" />
			)}
			{dropPosition === 'after' && (
				<Box position="absolute" bottom="-2px" left="0" right="0" height="4px" bg="purple.500" zIndex="10" borderRadius="full" pointerEvents="none" />
			)}
			<Tooltip label={contentMeta.type === 'video' ? (hasVideo ? `Video Attached: ${contentMeta.videoTitle || 'Linked'}` : "No video attached yet (Click to select video)") : `${contentMeta.type} item`} placement="top" hasArrow>
				<Box color={iconColor} position="relative" display="flex" alignItems="center">
					<FontAwesomeIcon size="sm" icon={icon} />
					{!hasVideo && contentMeta.type === 'video' && (
						<Box 
							position="absolute" 
							top="-3px" 
							right="-4px" 
							w="7px" 
							h="7px" 
							borderRadius="full" 
							bg="red.500" 
							border="1px solid white" 
							_dark={{ borderColor: "gray.800" }} 
						/>
					)}
				</Box>
			</Tooltip>
			{contentMeta.premium ? (
				<FontAwesomeIcon size="sm" icon={faCrown} color={premiumColor} />
			) : (<></>)}
			{newName === undefined && !confirmDeletion && (
				<>
					<Text textAlign='left' flex={1} overflow='hidden' textOverflow="ellipsis" whiteSpace="nowrap" title={contentMeta.title}>{contentMeta.title}</Text>
					{tagCount > 0 && (
						<Tooltip label={`${tagCount} markup tag(s) created`} placement="top" hasArrow>
							<Badge 
								colorScheme="purple" 
								variant="subtle" 
								fontSize="10px" 
								borderRadius="full" 
								px={1.5} 
								py={0.2} 
								display="flex" 
								alignItems="center" 
								gap={1}
								shadow="xs"
							>
								<FontAwesomeIcon icon={faTags} style={{ fontSize: '9px' }} />
								{tagCount}
							</Badge>
						</Tooltip>
					)}
					<Box onClick={(e) => e.stopPropagation()}>
						<Menu>
							<MenuButton as={IconButton} size='xs' aria-label="Section Options" icon={<FontAwesomeIcon icon={faEllipsis} />} />
							<Portal>
									<MenuList
										bg={useColorModeValue("rgba(255, 255, 255, 0.85)", "rgba(26, 32, 44, 0.85)")}
										backdropFilter="blur(15px)"
										border="1px solid"
										borderColor={useColorModeValue("rgba(226, 232, 240, 0.8)", "rgba(74, 85, 104, 0.8)")}
										borderRadius="xl"
										boxShadow="xl"
										p={1.5}
										minW="200px"
										maxH="300px"
										overflowY="auto"
									>
									<Text px={3} py={1} fontSize="10px" fontWeight="black" color="purple.500" _dark={{ color: "purple.300" }} textTransform="uppercase" letterSpacing="wider">
										Modify
									</Text>
									<MenuItem
										icon={<FontAwesomeIcon icon={faPen} />}
										onClick={() => setNewName(contentMeta.title)}
										borderRadius="lg"
										_hover={{ bg: useColorModeValue("purple.50", "rgba(128, 90, 213, 0.15)"), color: useColorModeValue("purple.700", "purple.200") }}
									>
										Rename
									</MenuItem>

									<MenuDivider borderColor={useColorModeValue("gray.100", "gray.700")} />

									<Text px={3} py={1} fontSize="10px" fontWeight="black" color="yellow.600" _dark={{ color: "yellow.300" }} textTransform="uppercase" letterSpacing="wider">
										Access Control
									</Text>
									<MenuItem
										icon={<FontAwesomeIcon icon={faCrown} />}
										onClick={() => onChange({ ...contentMeta, premium: 1 })}
										borderRadius="lg"
										_hover={{ bg: useColorModeValue("yellow.50", "rgba(236, 201, 75, 0.15)"), color: useColorModeValue("yellow.700", "yellow.200") }}
									>
										Set Level 1
									</MenuItem>
									<MenuItem
										icon={<FontAwesomeIcon icon={faCrown} />}
										onClick={() => onChange({ ...contentMeta, premium: 2 })}
										borderRadius="lg"
										_hover={{ bg: useColorModeValue("yellow.50", "rgba(236, 201, 75, 0.15)"), color: useColorModeValue("yellow.700", "yellow.200") }}
									>
										Set Level 2
									</MenuItem>
									<MenuItem
										icon={<FontAwesomeIcon icon={faCrown} />}
										onClick={() => onChange({ ...contentMeta, premium: 3 })}
										borderRadius="lg"
										_hover={{ bg: useColorModeValue("yellow.50", "rgba(236, 201, 75, 0.15)"), color: useColorModeValue("yellow.700", "yellow.200") }}
									>
										Set Level 3
									</MenuItem>
									{contentMeta.premium && (
										<MenuItem
											icon={<FontAwesomeIcon icon={faTimes} />}
											onClick={() => onChange({ ...contentMeta, premium: undefined })}
											borderRadius="lg"
											_hover={{ bg: useColorModeValue("gray.100", "rgba(255, 255, 255, 0.1)"), color: useColorModeValue("gray.700", "gray.300") }}
										>
											Clear Level
										</MenuItem>
									)}

									<MenuDivider borderColor={useColorModeValue("gray.100", "gray.700")} />

									<MenuItem
										icon={<FontAwesomeIcon icon={faTrash} />}
										onClick={() => setConfirmDeletion(true)}
										borderRadius="lg"
										color="red.500"
										_hover={{ bg: useColorModeValue("red.50", "rgba(229, 62, 62, 0.15)"), color: "red.600" }}
									>
										Delete
									</MenuItem>
								</MenuList>
							</Portal>
						</Menu>
					</Box>
				</>
			)}
			{newName !== undefined && (
				<Flex w='full' gap={1} align="center" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
					<Input size='xs' variant="outline" w="full" placeholder='Content Title' autoFocus={true} value={newName} onChange={(e) => setNewName(e.target.value)} bg="white" _dark={{ bg: "gray.800" }} />
					<IconButton size='xs' isDisabled={!isValid} variant='solid' colorScheme="blue" aria-label="Confirm Content Edit" icon={<FontAwesomeIcon icon={faCheck} />} onClick={(e) => {
						e.stopPropagation()
						onChange({ ...contentMeta, title: newName })
						setNewName(undefined)
					}} />
					<IconButton size='xs' variant='ghost' colorScheme="red" aria-label="Cancel Content Edit" icon={<FontAwesomeIcon icon={faCancel} />} onClick={(e) => {
						e.stopPropagation()
						setNewName(undefined)
					}} />
				</Flex>
			)}
			{confirmDeletion && (
				<Flex w='full' gap={1} align="center" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
					<Text fontSize="xs" color="red.600" _dark={{ color: "red.200" }} flex={1} textAlign="left" fontWeight="bold">Delete?</Text>
					<IconButton size='xs' variant='solid' colorScheme="red" aria-label="Confirm Content Deletion" icon={<FontAwesomeIcon icon={faCheck} />} onClick={(e) => {
						e.stopPropagation()
						onDelete(index)
						setConfirmDeletion(false)
					}} />
					<IconButton size='xs' variant='ghost' colorScheme="gray" aria-label="Cancel Content Deletion" icon={<FontAwesomeIcon icon={faCancel} />} onClick={(e) => {
						e.stopPropagation()
						setConfirmDeletion(false)
					}} />
				</Flex>
			)}
		</Button>
	)
}

const ContentCard = memo(ContentCardComponent)

export default ContentCard
